import { useContext, useEffect, useRef, useState } from "react";

import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { ShareDBContext } from "providers/ShareDBProvider";
import { store } from "store/index";
import { selectCurrentUserId } from "store/selectors";
import { getCombinedConnectionStatus, isDisconnectedStatus } from "utils/connection-status";
import { logInfoWithDebugFlag } from "utils/logger";
import { getWindowId } from "utils/window";

// Delay before surfacing a disconnect so transient blips don't flash UI unnecessarily.
const BANNER_SHOW_DELAY_MS = 3_000;
// Time before escalating from "reconnecting" to "retrying" to signal a prolonged outage.
const RETRYING_THRESHOLD_MS = 10_000;

export type ConnectionAlertStatus = "reconnecting" | "retrying" | "failed";

/**
 * Observes the combined websocket connection state (pubsub + ShareDB) and returns
 * a user-facing alert status, or null when there is no alert.
 *
 * Progresses through three statuses based on elapsed time and whether retries
 * have been exhausted:
 *
 *   reconnecting  — first 0-10 seconds after appearing
 *   retrying      — 11+ seconds, still attempting
 *   failed        — retries exhausted, page reload required
 *
 * Waits 3 seconds before becoming non-null so transient blips are ignored.
 * All timers reset only when the connection is fully restored.
 */
export const useConnectionAlertStatusObserver = (): ConnectionAlertStatus | null => {
  const shareDBContext = useContext(ShareDBContext);
  const { pubsubStatus } = useContext(PubsubWebsocketContext);
  const shareDBStatus = shareDBContext?.shareDBStatus ?? null;

  const combinedStatus = getCombinedConnectionStatus(shareDBStatus, pubsubStatus);

  const [alertStatus, setAlertStatus] = useState<ConnectionAlertStatus | null>(null);
  const showTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const retryTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const resetTimers = () => {
    clearTimeout(showTimerRef.current);
    clearTimeout(retryTimerRef.current);
    showTimerRef.current = undefined;
  };

  useEffect(() => {
    if (combinedStatus === "connected") {
      resetTimers();
      setAlertStatus(null);
      return;
    }

    if (combinedStatus === "failed") {
      resetTimers();
      setAlertStatus("failed");
      return;
    }

    if (
      isDisconnectedStatus(combinedStatus) &&
      showTimerRef.current === undefined &&
      alertStatus === null
    ) {
      const userId = selectCurrentUserId(store.getState());
      const windowId = getWindowId();

      if (shareDBStatus !== null && isDisconnectedStatus(shareDBStatus)) {
        logInfoWithDebugFlag("debug_sharedb", "Shared DB Websocket disconnected", {
          userId,
          windowId,
          shareDBStatus,
        });
      }

      if (isDisconnectedStatus(pubsubStatus)) {
        logInfoWithDebugFlag("debug_pubsub", "Pubsub Websocket disconnected", {
          userId,
          windowId,
          pubsubStatus,
        });
      }

      showTimerRef.current = setTimeout(() => {
        showTimerRef.current = undefined;
        setAlertStatus("reconnecting");
        retryTimerRef.current = setTimeout(
          () => setAlertStatus((status) => (status === "reconnecting" ? "retrying" : status)),
          RETRYING_THRESHOLD_MS,
        );
      }, BANNER_SHOW_DELAY_MS);
    }
  }, [combinedStatus, alertStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  return alertStatus;
};
